import {InjectionToken} from '@angular/core';
import type {DrmConfig} from './rtc-drm-transform/rtc-drm-transform';
import {PlatformInfo} from './rtc-drm-transform/platformChecks';
import {mseHasAv1, SAFARI_AV1_OFF, safariAv1Off, webRtcReceivesAv1} from './crd-av1';

/**
 * VTK-3622: the viewer's only contact with rtc-drm-transform, whose sources are vendored in ./rtc-drm-transform
 * (see its README.md). Nothing else imports the library; RTC_DRM_LIBRARY lets the specs replace it.
 * An encrypted mode never falls back to clear playback.
 */

/** The DRM mode of the environment, as the licence-token query reports it. */
export type CrdDrmMode = 'CLEAR' | 'AUDIO' | 'VIDEO' | 'AV';

/** One encrypted track: hex key id and IV, the encryption scheme and the codec the host encrypts. */
export interface CrdDrmTrack {
  kid: string;
  iv: string;
  encryption: 'cbcs' | 'cenc';
  /** Video: H264 or AV1 (4:2:0, a host tagged VIDEO_CODECS=AV1; binary repo docs/av1-drm.md); audio: opus. */
  codec?: string;
}

/** The codec the session's encrypted video track carries: H264 unless the backend says otherwise. */
export function videoCodec(session: CrdDrmSession): string {
  return session.video?.codec || 'H264';
}

/** What the backend hands the viewer for one session (VTK-3622 licence-token query). */
export interface CrdDrmSession {
  mode: CrdDrmMode;
  token: string | null;
  tokenExpiresAt: string | null;
  merchant: string | null;
  environment: string | null;
  /** rtc-drm-transform's target media buffer for this env (deploy-time setting); null iff CLEAR. */
  mediaBufferMs: number | null;
  video: CrdDrmTrack | null;
  audio: CrdDrmTrack | null;
}

/** The part of rtc-drm-transform the viewer uses. */
export interface RtcDrmLibrary {
  configure(config: Record<string, unknown>): void;
  onTrack(event: RTCTrackEvent, config: Record<string, unknown>): void;
  /** Renews the token in the licence request headers of the instance on this video element. */
  setAuthToken(video: HTMLVideoElement, token: string): void;
  /** Forgets the instance on this video element, so the next configure() builds a fresh one. */
  dispose(video: HTMLVideoElement): void;
  environments: {Production: unknown; Staging: unknown};
  version(): string;
}

/** localStorage key for a test-only mediaBufferMs override (VTK-3622 latency A/B); ignored in prod builds. */
export const MEDIA_BUFFER_OVERRIDE_KEY = 'crd-drm-media-buffer-ms';

/**
 * The mediaBufferMs override from localStorage, or null if none is set. Honoured only outside prod builds
 * (environment.production false). A value that is not a non-negative integer stops the session rather than
 * being ignored, so a typo in an A/B run cannot silently measure the default instead.
 */
export function mediaBufferOverride(production: boolean, storage: Pick<Storage, 'getItem'> | null): number | null {
  if (production || !storage) return null;
  let raw: string | null;
  try {
    raw = storage.getItem(MEDIA_BUFFER_OVERRIDE_KEY);
  } catch {
    return null;   // storage blocked (private mode, policy): no override
  }
  if (raw === null) return null;
  if (!/^\d+$/.test(raw.trim())) {
    throw new DrmUnavailable(`localStorage ${MEDIA_BUFFER_OVERRIDE_KEY} is '${raw}', not a whole number of milliseconds`);
  }
  return parseInt(raw, 10);
}

/** Thrown for anything that stops an encrypted session; the message follows "DRM unavailable: ". */
export class DrmUnavailable extends Error {}

/** Loads the library on first use, as its own chunk: only an encrypted CRD session needs it. */
export const RTC_DRM_LIBRARY = new InjectionToken<() => Promise<RtcDrmLibrary>>('rtc-drm-transform', {
  providedIn: 'root',
  factory: () => async () => {
    const lib = await import('./rtc-drm-transform/rtc-drm-transform');
    return {
      configure: (config) => lib.rtcDrmConfigure(config as unknown as DrmConfig),
      onTrack: (event, config) => lib.rtcDrmOnTrack(event, config as unknown as DrmConfig),
      setAuthToken: (video, token) => lib.rtcDrmSetAuthToken(video, token),
      dispose: (video) => lib.rtcDrmDispose(video),
      environments: lib.rtcDrmEnvironments,
      version: lib.rtcDrmGetVersion,
    };
  },
});

/** Which kinds the mode encrypts. */
export function encryptedKinds(mode: CrdDrmMode): Set<'video' | 'audio'> {
  return new Set(({CLEAR: [], AUDIO: ['audio'], VIDEO: ['video'], AV: ['video', 'audio']} as const)[mode]);
}

/**
 * Checks that the session is complete for its mode and the browser can play it. Throws DrmUnavailable with
 * the reason otherwise. A CLEAR session needs nothing.
 */
export function checkDrmSession(session: CrdDrmSession, win: Window = window, isSafari = PlatformInfo.isSafari): void {
  const kinds = encryptedKinds(session.mode);
  if (kinds.size === 0) return;
  if (!session.token) throw new DrmUnavailable('no licence token');
  if (!Number.isInteger(session.mediaBufferMs) || session.mediaBufferMs! < 0) {
    throw new DrmUnavailable(`no valid media buffer setting (${session.mediaBufferMs})`);
  }
  for (const kind of kinds) {
    if (!session[kind]) throw new DrmUnavailable(`no key for the ${kind} track`);
  }
  // rtc-drm-transform cannot play encrypted Opus on Safari: it would silently drop the audio (ignoreAudio)
  if (isSafari && kinds.has('audio')) {
    throw new DrmUnavailable('this browser cannot play encrypted audio (Safari); this environment encrypts it');
  }
  if (!('requestMediaKeySystemAccess' in win.navigator)) {
    throw new DrmUnavailable('this browser has no Encrypted Media Extensions');
  }
  const receiver = (win as any).RTCRtpReceiver?.prototype;
  if (!(receiver && 'createEncodedStreams' in receiver) && !('RTCRtpScriptTransform' in win)) {
    throw new DrmUnavailable('this browser has no WebRTC Encoded Transform');
  }
  // An AV1 host offers nothing else: without AV1 in WebRTC (Firefox, Safari without hardware AV1, a Safari window
  // hit by safariAv1Off) the picture would just stay black, and MSE has to take the decrypted stream
  if (kinds.has('video') && videoCodec(session) === 'AV1') {
    if (!webRtcReceivesAv1(win)) {
      throw new DrmUnavailable(safariAv1Off(win, isSafari)
        ? SAFARI_AV1_OFF
        : 'this browser cannot receive AV1 over WebRTC; this host encrypts AV1');
    }
    if (!mseHasAv1(win)) {
      throw new DrmUnavailable('this browser cannot play AV1 through Media Source Extensions; this host encrypts AV1');
    }
  }
}

/** The encrypted part of a viewer connection: configures the library and routes each incoming track. */
export class DrmPlayback {
  private readonly config: Record<string, unknown>;
  private readonly kinds: Set<'video' | 'audio'>;
  private readonly video: HTMLVideoElement;

  /**
   * @param mediaBufferOverride a test-only replacement for the session's mediaBufferMs (see mediaBufferOverride).
   */
  constructor(private readonly library: RtcDrmLibrary, session: CrdDrmSession,
              video: HTMLVideoElement, audio: HTMLAudioElement, mediaBufferOverride: number | null = null) {
    this.kinds = encryptedKinds(session.mode);
    this.video = video;
    // The library keeps one instance per video element and reuses it; a reconnect brings new keys and a new token.
    library.dispose(video);
    const track = (t: CrdDrmTrack | null, codec: string) => t
      ? {codec, encryption: t.encryption, robustness: 'SW', keyId: hexToBytes(t.kid), iv: hexToBytes(t.iv)}
      : {codec, encryption: 'clear'};
    this.config = {
      merchant: session.merchant,
      environment: session.environment === 'production' ? library.environments.Production : library.environments.Staging,
      authToken: session.token,
      videoElement: video,
      audioElement: audio,
      video: track(this.kinds.has('video') ? session.video : null, videoCodec(session)),
      audio: track(this.kinds.has('audio') ? session.audio : null, 'opus'),
      mediaBufferMs: mediaBufferOverride ?? session.mediaBufferMs,
    };
    library.configure(this.config);
  }

  /**
   * Hands a track to the library -- every track of an encrypted session, the clear one too (as upstream's
   * reference client does): the library plays a clear audio track itself and holds it back to the video's
   * buffered latency (setAudioDelay), which keeps picture and sound in sync.
   */
  onTrack(event: RTCTrackEvent): void {
    // WebRTC allows a track without a stream; the library attaches a clear track as event.streams[0].
    const withStream = event.streams.length > 0 ? event : {
      track: event.track, receiver: event.receiver, transceiver: event.transceiver,
      streams: [new MediaStream([event.track])],
    } as unknown as RTCTrackEvent;
    this.library.onTrack(withStream, this.config);
  }

  /** A fresh token for the licence requests still to come (reconnects use a fresh session anyway). */
  updateToken(token: string): void {
    this.config['authToken'] = token;
    this.library.setAuthToken(this.video, token);
  }

  /** Ends this connection's library instance (its MSE, workers and licence headers). */
  dispose(): void {
    this.library.dispose(this.video);
  }
}

export function hexToBytes(hex: string): Uint8Array {
  if (!/^([0-9a-fA-F]{2})+$/.test(hex)) throw new DrmUnavailable('malformed key id or IV');
  return new Uint8Array(hex.match(/../g)!.map(b => parseInt(b, 16)));
}
